import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Style } from './style';
import Widget from '../../widget';
import { CustomIcon } from 'datastake-daf/dist/components';
import StatusTag from '../../../../Tags/StatusTag';
import { LoadingOutlined } from '@ant-design/icons';
import { formatClassname } from 'datastake-daf/src/helpers/ClassesHelper';
import { useCountry } from '../../../../../helpers/map';

const loader = (
  <div className="loading-ic flex-c jus-c">
    <LoadingOutlined />
  </div>
);
const Determination = ({ data = null, projectData }) => {
  const { t } = useTranslation();
  const loading = !data;
  const { getCountry, getCountryDetemination } = useCountry();
  const multipleCountryStatus = useCallback((countries) => {
    const determinations = Array.isArray(countries) && countries.length ? (countries.filter(c => c)).reduce((all, country) => {
      const determination = getCountryDetemination(country);
      if (!all.includes(determination) && determination !== 'Undetermined') {
        all.push(determination);
      }
      return all;
    }, []) : [];
    let determination = 'Undetermined';
    if (determinations.length) {
      if (determinations.length === 1 || determinations.every(t => t === 'Low Risk')) {
        determination = determinations[0];
      } else {
        determination = determinations.filter(d => d !== 'Low Risk')[0];
      }
    }
    const type = determination === 'Low Risk' ? 'success' : (determination === 'Undetermined' ? 'medium' : '')
    return <div className="status-c">
      <StatusTag type={type}>{t(determination)}</StatusTag>
    </div>
  }, [t, getCountryDetemination]);

  const isHighRisk = useMemo(() => {
    if (projectData && data && !loading) {
      const countries = [...new Set([
        projectData.country,
        ...(Array.isArray(data?.countriesOfMineralTransit) ? data?.countriesOfMineralTransit : []),
        ...(Array.isArray(data?.associatedCountries) ? data?.associatedCountries : []),
      ].filter(c => c))].reduce((all, country) => {
        const determination = getCountryDetemination(country);
        if (!all.includes(determination) && determination !== 'Undetermined') {
          all.push(determination);
        }
        return all;
      }, []);
      if (countries.find(d => d === 'CAHRA')) {
        return true;
      }
      return false;
    }
    return false;
  }, [data, projectData, getCountryDetemination, loading]);

  return (
    <Widget
      title={t('Determination')}
    >
      <Style>
        <div>
          <label>{t('Country of Origin')}</label>
          <div className="flex-row jus-b">
            {(loading || !projectData) ? loader : (
              <div className="tag">
                {projectData && projectData?.country ? getCountry(projectData.country) : t('Loading')}
              </div>
            )}
            {loading || !projectData?.country ? loader : multipleCountryStatus([projectData.country])}
          </div>
        </div>
        <div>
          <label>{t('Countries of Mineral Transit')}</label>
          <div className="flex-row jus-b flex-1 w-gap">
            {loading ? loader : (
              <div className="flex-1">
                {Array.isArray(data?.countriesOfMineralTransit) && data?.countriesOfMineralTransit.length ? (
                  <div className="flex-wrap flex-row list-c">
                    {data?.countriesOfMineralTransit.map((c, i) => (
                      <div className="tag" key={`countries-${c}-${i}`}>
                        {getCountry(c)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    <span>--</span>
                  </div>
                )}
              </div>
            )}
            {loading ? loader : multipleCountryStatus(data?.countriesOfMineralTransit || [])}
          </div>
        </div>
        <div>
          <label>{t('Associated Countries')}</label>
          <div className="flex-row jus-b flex-1 w-gap">
            {loading ? loader : (
              <div className="flex-1">
                {Array.isArray(data?.associatedCountries) && data?.associatedCountries.length ? (
                  <div className="flex-wrap flex-row list-c">
                    {data?.associatedCountries.map((c, i) => (
                      <div className="tag" key={`countries-${c}-${i}`}>
                        {getCountry(c)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    <span>--</span>
                  </div>
                )}
              </div>
            )}
            {loading ? loader : multipleCountryStatus(data?.associatedCountries || [])}
          </div>
        </div>

        <div className={formatClassname(['message-box', loading && 'hidden'])}>
          <div className="flex-c jus-c">
            <div className="icon-cont">
              <CustomIcon name="Warning" width={20} height={20} />
            </div>
          </div>
          <div className="flex-1 flex-c jus-c">
            <p>
              {
                isHighRisk ?
                  t('Enhanced due diligence is mandatory. Mineral buyers are expected to actively seek information to monitor the improvement of supply chain circumstances, and participate in the proposed risk mitigation.')
                  :
                  t('Enhanced due diligence is recommended but not mandatory. Mineral buyers are invited to regularly review information available on supply chain circumstances.')
                }
            </p>
          </div>
        </div>
      </Style>
    </Widget >
  );
};

export default Determination;
